import { Icon, Text, Tooltip, HStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { CoeOfferDetailEntity } from '@genial/services';
import { Utils } from '@genial/utils';
import { Dimensions } from 'react-native';
import * as zod from 'zod';

import { createArrayWithBooleans } from '../../Product/components/Funds/constants';
import {
  VariationStyleValueProp,
  getVariationStyle,
} from '../../Product/components/StructuredOperations/constants';

const { height: windowHeight } = Dimensions.get('window');

type ValidatedValue = 0 | 1 | -1;

interface Style {
  color: string;
  icon: string;
}

interface MiddlePercentageData {
  label?: string | null;
  x?: number | null;
  y?: number | null;
}

export const height = windowHeight * 0.75;
export const messageError = '―';

export const getColorAndIconOperation = (value: number): Style => {
  const validatedValue = Math.sign(value) as ValidatedValue;

  const style: Record<ValidatedValue, Style> = {
    0: {
      color: 'brand.primary.principal',
      icon: 'ArrowRight',
    },
    1: {
      color: 'feedback.success.default',
      icon: 'ArrowUp',
    },
    '-1': {
      color: 'feedback.error.default',
      icon: 'ArrowDown',
    },
  };

  return style[validatedValue];
};

const defaultPointStartChart = { x: 0, y: 0, label: messageError };

export const getStartPointChart = (arr: MiddlePercentageData[]) => {
  if (!Array.isArray(arr) || arr.length === 0) {
    return defaultPointStartChart;
  }

  const startPointIndex: number = arr.findIndex((item) => item.x === 0);

  return arr[startPointIndex] || defaultPointStartChart;
};

export const parseStructuredOperationContentHorizontalData = (value: CoeOfferDetailEntity) => {
  const fieldsDataComponent = (value?.fields?.slice(0, 2).map((field) => field) || []).map(
    (field) => {
      const variationStyle = getVariationStyle(
        (field?.displaTypeId as VariationStyleValueProp) || 'error'
      );

      return {
        label: field?.description || messageError,
        value: (
          <HStack alignItems={'center'} space={Spacing['inline.quarck']}>
            {variationStyle && variationStyle?.name && variationStyle?.color && (
              <Icon
                name={variationStyle?.name}
                color={Color[variationStyle?.color]}
                size="xxxxs"
                testID="icon-arrow"
              />
            )}
            <Text color={'neutral.darkest'} variant="regular" fontSize={Typography['fontsize.md']}>
              {Utils.Mask.Percentage(field?.value || 0)}
            </Text>
          </HStack>
        ),
      };
    }
  );

  const riskDataComponent = {
    label: 'Risco',
    value: (
      <Tooltip testID="qa" label={value?.suitabilityRisk?.risk || ''}>
        <HStack space={Spacing['inline.nano']} maxW={'110px'}>
          {createArrayWithBooleans(value?.suitabilityRisk?.scoreAdjusted || 0).map(
            (riskRating, index) => (
              <Icon
                key={index.toString()}
                testID="qa"
                name="Circle"
                size="xxxxxs"
                color={Color[riskRating ? 'neutral.darkest' : 'neutral.lightest']}
              />
            )
          )}
        </HStack>
      </Tooltip>
    ),
  };

  const dateValidityIsValid = value?.finishDateValidity && value?.startDateValidity;

  const startHourValidity =
    dateValidityIsValid && Utils.Mask.DateFormat(value?.startDateValidity as Date, 'HH:mm');

  const finishHourValidity =
    dateValidityIsValid && Utils.Mask.DateFormat(value?.finishDateValidity as Date, 'HH:mm');

  const availabilityHours = dateValidityIsValid
    ? `${startHourValidity} às ${finishHourValidity}`
    : messageError;

  const parseStructuredOperationContent = [
    ...fieldsDataComponent,
    {
      label: 'Aplicação em',
      value: value?.applicationIn || messageError,
    },
    {
      label: 'Vencimento',
      value: value?.maturityDate
        ? Utils.Mask.DateFormat(value?.maturityDate, 'dd/MM/yyyy')
        : messageError,
    },
    riskDataComponent,
    {
      label: 'Horário de disponibilidade',
      value: availabilityHours,
    },
  ];

  return parseStructuredOperationContent;
};

export const maximumAmount = 1000000;

export type appliedAmountFormData = {
  appliedAmount: string;
};

export const validationInvestmentSchema = (minValue: number) => {
  return zod.object({
    appliedAmount: zod
      .string({
        required_error: `Valor mínimo ${Utils.Mask.Money(minValue)}`,
      })
      .refine(
        (currentValue) => Utils.Unmask.Money(currentValue) >= Number(minValue?.toFixed(2) || 0),
        {
          message: `Valor mínimo ${Utils.Mask.Money(minValue)}`,
        }
      )
      .refine((currentValue) => Utils.Unmask.Money(currentValue) <= maximumAmount, {
        message: `Valor máximo ${Utils.Mask.Money(maximumAmount)}`,
      }),
  });
};
